import { useState, useEffect } from "react";
import { useAppSelector } from "../../store/hooks";
import { selectIsAdmin } from "../../store/authSelectors";
import {
  useGetQuery,
  useAddMutation,
  useUpdateMutation,
  useRemoveMutation,
} from "../../store/slices/apiSlice";
import { useToast } from "./use-toast";
import { CreateLicenseData, EditLicenseData, License } from "../../store/types";
import { LicenseKind } from "../../types/licenseTypes";
import { toUTCStartOfDay } from "../dateTimeConvertion";
import { getErrorMessage } from "./use-getError";

/** Query-фильтры GET /api/v2/licenses */
export interface LicenseFilters {
  organizationId?: number | string | null;
  userId?: string | null;
  projectId?: string | null;
  kind?: LicenseKind | null;
  isEnabled?: boolean | null;
  isActive?: boolean | null;
}

/** Собирает адрес списка лицензий v2 с query-фильтрами */
export const buildLicensesEndpoint = (
  licensesEP: string,
  filters?: LicenseFilters
): string => {
  if (!licensesEP) return "";
  if (!filters) return licensesEP;

  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    params.append(key, String(value));
  });

  const query = params.toString();
  return query ? `${licensesEP}?${query}` : licensesEP;
};

/** Тело CreateLicenseRequest (v2) из данных формы */
const toCreateLicenseRequest = (license: CreateLicenseData) => ({
  kind: license.kind,
  projectId: license.projectId,
  organizationId: license.organizationId,
  startsAt: toUTCStartOfDay(license.startsAt),
  expiresAt:
    license.kind === LicenseKind.Perpetual
      ? null
      : toUTCStartOfDay(license.expiresAt),
  maxActiveUsers:
    license.kind === LicenseKind.Session ? license.maxActiveUsers : null,
  comments: license.comments || null,
  count: license.count,
});

/** Тело UpdateLicenseRequest (v2) */
const toUpdateLicenseRequest = (license: EditLicenseData) => ({
  startsAt:
    license.startsAt instanceof Date
      ? toUTCStartOfDay(license.startsAt)
      : license.startsAt,
  expiresAt:
    license.expiresAt instanceof Date
      ? toUTCStartOfDay(license.expiresAt)
      : license.expiresAt,
  comments: license.comments || null,
});

/**
 * Приводит OldUserLicenseDto (GET /api/users/me/licenses) к модели v2.
 * Для лицензий текущего пользователя эндпоинта v2 нет, поэтому нормализуем ответ v1.
 */
export const fromLegacyUserLicense = (license: any): License => ({
  id: license.id,
  kind: license.kind ?? license.type,
  projectId: license.projectId,
  projectName: license.projectName,
  organizationId: license.organizationId,
  organizationName: license.organizationName,
  issuedAt: license.issuedAt ?? license.issueDate ?? null,
  startsAt: license.startsAt ?? license.startDate ?? null,
  expiresAt: license.expiresAt ?? license.expirationDate ?? null,
  isEnabled: license.isEnabled,
  isActive: license.isActive,
  maxActiveUsers: license.maxActiveUsers ?? null,
  comments: license.comments ?? null,
  seats: license.seats ?? null,
  seatCount: license.seatCount,
  onlineCount: license.onlineCount,
});

export const useLicensesAPI = (
  filters?: LicenseFilters,
  options?: { skipList?: boolean }
) => {
  const isAdmin = useAppSelector(selectIsAdmin);
  const licensesEP = useAppSelector((state) => state.endpoints.licenses);
  const [licenses, setLicenses] = useState<License[] | null>(null);
  const { toastRef, showSuccess, showWarning, showError } = useToast();

  const [addItem, { isLoading: isAdding }] = useAddMutation();
  const [editItem, { isLoading: isEditing }] = useUpdateMutation();
  const [deleteItem, { isLoading: isDeleting }] = useRemoveMutation();

  const listEndpoint = buildLicensesEndpoint(licensesEP, filters);

  const {
    data: fetchedLicenses,
    refetch: refetchLicenses,
    isLoading: isFetchLoading,
  } = useGetQuery(listEndpoint, {
    refetchOnMountOrArgChange: true,
    skip: !listEndpoint || !isAdmin || options?.skipList === true,
  });

  const refetchData = async () => {
    await refetchLicenses();
  };

  const handleError = (operation: string, error: any) => {
    showError(
      "Ошибка",
      `Не удалось ${operation}, ошибка: ${error}. Попробуйте позже.`
    );
  };

  const reportError = (operation: string, error: any) => {
    const serverError = error ? getErrorMessage(error) : undefined;
    handleError(operation, serverError ?? error);
  };

  /** POST /api/v2/licenses — возвращает массив id созданных лицензий */
  const handlePostLicense = async (
    license: CreateLicenseData
  ): Promise<{ success: boolean; licenseIds?: string[] }> => {
    try {
      const result = await addItem({
        endpoint: licensesEP,
        id: "",
        body: toCreateLicenseRequest(license),
      });
      if (result.data) {
        showSuccess("Успешно", "Лицензия создана");
        const licenseIds = (result.data as string[]) ?? [];
        return { success: true, licenseIds };
      }

      reportError("создать лицензию", result.error);
      return { success: false };
    } catch (error) {
      handleError("создать лицензию", error);
      return { success: false };
    }
  };

  /** POST /api/v2/licenses/{id}/enable */
  const handleActivateLicense = async (id: string): Promise<boolean> => {
    try {
      const result = await addItem({
        endpoint: licensesEP,
        id: `${id}/enable`,
      });
      if (result.data) {
        showSuccess("Успешно", "Лицензия активирована");
        return true;
      }

      reportError("активировать лицензию", result.error);
      return false;
    } catch (error) {
      handleError("активировать лицензию", error);
      return false;
    }
  };

  /** POST /api/v2/licenses/{id}/disable */
  const handleDeactivateLicense = async (id: string): Promise<boolean> => {
    try {
      const result = await addItem({
        endpoint: licensesEP,
        id: `${id}/disable`,
      });
      if (result.data) {
        showSuccess("Успешно", "Лицензия деактивирована");
        return true;
      }

      reportError("деактивировать лицензию", result.error);
      return false;
    } catch (error) {
      handleError("деактивировать лицензию", error);
      return false;
    }
  };

  /** PUT /api/v2/licenses/{id} */
  const handleUpdateLicense = async (
    license: EditLicenseData,
    licenseId: string
  ): Promise<boolean> => {
    try {
      const result = await editItem({
        endpoint: licensesEP,
        id: licenseId,
        body: toUpdateLicenseRequest(license),
      });
      if (result.data) {
        showSuccess("Успешно", "Данные лицензии обновлены");
        return true;
      }

      reportError("обновить лицензию", result.error);
      return false;
    } catch (error) {
      handleError("обновить лицензию", error);
      return false;
    }
  };

  /** PUT /api/v2/licenses/{licenseId}/seats/{userId} — назначить пользователя на место */
  const handleAssignSeat = async (
    licenseId: string,
    userId: string
  ): Promise<boolean> => {
    try {
      const result = await editItem({
        endpoint: licensesEP,
        id: `${licenseId}/seats/${userId}`,
      });
      if (result.data) {
        showSuccess("Успешно", "Пользователь добавлен в лицензию");
        return true;
      }

      reportError("добавить пользователя в лицензию", result.error);
      return false;
    } catch (error) {
      handleError("добавить пользователя в лицензию", error);
      return false;
    }
  };

  /** DELETE /api/v2/licenses/{licenseId}/seats/{userId} — освободить место */
  const handleRemoveSeat = async (
    licenseId: string,
    userId: string
  ): Promise<boolean> => {
    try {
      const result = await deleteItem({
        endpoint: licensesEP,
        id: `${licenseId}/seats/${userId}`,
      });
      if (result.error) {
        reportError("удалить пользователя из лицензии", result.error);
        return false;
      }

      showSuccess("Успешно", "Пользователь удален из лицензии");
      return true;
    } catch (error) {
      handleError("удалить пользователя из лицензии", error);
      return false;
    }
  };

  /** POST /api/v2/licenses/{licenseId}/seats/{userId}/reset — сброс привязки к оборудованию */
  const handleResetSeat = async (
    licenseId: string,
    userId: string
  ): Promise<boolean> => {
    try {
      const result = await addItem({
        endpoint: licensesEP,
        id: `${licenseId}/seats/${userId}/reset`,
      });
      if (result.error) {
        reportError("сбросить информацию об оборудовании", result.error);
        return false;
      }

      showSuccess("Успешно", "Информация об оборудовании сброшена");
      return true;
    } catch (error) {
      handleError("сбросить информацию об оборудовании", error);
      return false;
    }
  };

  /** PUT /api/v2/licenses/{id}/max-active-users */
  const handleEditMaxActiveUsers = async (
    licenseId: string,
    maxActiveUsers: number
  ): Promise<boolean> => {
    try {
      const result = await editItem({
        endpoint: licensesEP,
        id: `${licenseId}/max-active-users`,
        body: { maxActiveUsers },
      });
      if (result.error) {
        reportError("изменить количество активных пользователей", result.error);
        return false;
      }

      showSuccess("Успешно", "Количество активных пользователей изменено.");
      return true;
    } catch (error) {
      handleError("изменить количество активных пользователей", error);
      return false;
    }
  };

  /** DELETE /api/v2/licenses/{id} */
  const handleDeleteLicense = async (license: License): Promise<boolean> => {
    try {
      const result = await deleteItem({
        endpoint: licensesEP,
        id: license?.id,
      });
      if (result.error) {
        reportError("удалить лицензию", result.error);
        return false;
      }

      showSuccess("Успешно", "Лицензия удалена");
      return true;
    } catch (error) {
      handleError("удалить лицензию", error);
      return false;
    }
  };

  useEffect(() => {
    if (fetchedLicenses) setLicenses(fetchedLicenses);
  }, [fetchedLicenses]);

  return {
    licensesEP,
    isFetchLoading,
    handlePostLicense,
    handleActivateLicense,
    handleDeactivateLicense,
    handleUpdateLicense,
    handleAssignSeat,
    handleRemoveSeat,
    handleResetSeat,
    handleDeleteLicense,
    handleEditMaxActiveUsers,
    refetchData,
    licenses,
    isAdding,
    isEditing,
    isDeleting,
    toastRef,
    showSuccess,
    showError,
    showWarning,
    handleError,
  };
};
